/**
 * TranscribeService — push-to-talk speech-to-text using AWS Transcribe Streaming.
 *
 * Why this exists:
 *   The browser-native Web Speech API (window.SpeechRecognition) isn't shipped
 *   by Firefox at all and is gated behind flags on iOS Safari, so the previous
 *   useDictation hook silently failed on those platforms. This ports the
 *   architecture used by SecurityBot-dev's TranscribeService:
 *
 *     getUserMedia (16 kHz PCM mono)
 *       → AudioContext  (16 kHz)
 *       → AudioWorkletNode (or ScriptProcessor fallback)
 *       → Float32 → Int16 PCM
 *       → ReadableStream<AudioStream>
 *       → Transcribe Streaming WebSocket
 *
 *   AWS creds are obtained from the Cognito Identity Pool whose config we
 *   fetch from `/cima/voice/transcribe-config` — same pattern SecurityBot uses.
 *
 *   The Web Speech API is kept as a graceful fallback for browsers where
 *   getUserMedia isn't available or the Cognito init fails (e.g. local dev
 *   before the SAM stack updates).
 *
 * Adapted from SecurityBot-dev/frontend-v2/src/services/voice/TranscribeService.ts
 * with CIMA-specific simplifications:
 *   - en-AU only (no per-tenant locale)
 *   - No wake lock (web-only, desk-bound IC)
 *   - Slimmed silence detection — caller decides when to commit
 */
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
  LanguageCode,
  MediaEncoding,
  type AudioStream,
} from '@aws-sdk/client-transcribe-streaming';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import type { TranscribeConfig } from '../../types/voice';
import { getTranscribeConfig } from '../../api/voice';

/**
 * Map any voice-init / streaming failure into a short, user-friendly
 * sentence the chat can render in its error banner. Real engineers
 * still see the raw error in the console (we never swallow it). This
 * stops cryptic AWS SDK strings like "undefined Deserialization error:
 * to see the raw response, inspect the hidden field {error}.$response
 * on this object." from reaching end users.
 *
 * Recognised buckets:
 *   • Mic permission denied / blocked / dismissed
 *   • No mic hardware
 *   • Browser doesn't support getUserMedia / Web Audio
 *   • Cognito creds couldn't be fetched (auth/network)
 *   • Transcribe streaming connection blocked (CSP, firewall, network)
 *   • SDK deserialization error (covers the recent CSP-port-8443 bug)
 *   • Generic fallback when nothing matches
 */
function friendlyVoiceError(err: unknown): string {
  const raw = (err instanceof Error ? err.message : String(err ?? '')).trim();
  const name = (err instanceof Error ? err.name : '') || '';
  const lc = (raw + ' ' + name).toLowerCase();

  // Mic permission / hardware
  if (/notallowed|permission denied|permissiondenied/.test(lc)) {
    return 'Microphone access was blocked. Click the lock icon in your browser’s address bar, allow mic access, and try again.';
  }
  if (/notfound|devicenotfound|no microphone|no audio input|no media/.test(lc)) {
    return 'No microphone found. Plug in or enable a mic on your device, then try again.';
  }
  if (/notreadable|trackerror|hardware/.test(lc)) {
    return 'Your microphone is in use by another app. Close that app (Zoom, Teams, etc.) and try again.';
  }
  if (/aborted/.test(lc) && /getusermedia|mediadevices/.test(lc)) {
    return 'Microphone access was cancelled. Tap the mic again when you’re ready.';
  }

  // Browser support
  if (/getusermedia|mediadevices|audiocontext|web audio|not supported/.test(lc)) {
    return 'Your browser doesn’t support voice input here. Try the latest Chrome, Edge, or Safari — or type your message instead.';
  }

  // Network / connection — the CSP :8443 bucket falls in here too
  if (/deserialization|deserializationerror|inspect the hidden field|\$response on this object/.test(lc)) {
    return 'Voice service couldn’t connect. This usually clears after a hard refresh (Cmd/Ctrl+Shift+R). If it keeps happening, type instead and let your admin know.';
  }
  if (/network|fetch|cors|failed to fetch|tls|websocket|http\/2|certificate/.test(lc)) {
    return 'Couldn’t reach the voice service — usually a network blip. Hit the mic again, or type your message instead.';
  }
  if (/cognito|credentials|identity pool|access denied|unauthorized|unauthorised/.test(lc)) {
    return 'Voice authentication failed. Refresh the page to re-acquire credentials, or type instead.';
  }
  if (/timeout|timed out|timed-out/.test(lc)) {
    return 'Voice connection timed out. Tap the mic again to retry, or type your message instead.';
  }

  // Server-side Transcribe limit / quota / language errors
  if (/throttle|throttling|limitexceeded|quotaexceeded|rate exceeded/.test(lc)) {
    return 'Voice service is busy right now. Wait a few seconds and try again.';
  }

  // Truly unknown — keep it short, don't expose stack/SDK noise
  return 'Voice didn’t start. Tap the mic to retry, or type your message instead.';
}

export class TranscribeService {
  private transcribeClient: TranscribeStreamingClient | null = null;
  private credentialProvider: any = null;
  private config: TranscribeConfig | null = null;
  private audioStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private streamController: ReadableStreamDefaultController<unknown> | null = null;
  private connectionActive = false;
  private isActive = false;
  private transcript = '';
  private workletRegistered = false;

  /** Falling back to browser SpeechRecognition? */
  private useFallback = false;
  private recognition: any = null;

  /* callbacks */
  private onInterimCb: ((text: string) => void) | null = null;
  private onErrorCb:   ((error: string) => void) | null = null;
  private onSilenceCb: (() => void) | null = null;
  /** Fired every SILENCE_TICK_MS while the silence timer is counting down.
   *  msLeft is the time remaining before auto-commit. Drives the visible
   *  countdown in the voice overlay so users know they can take their
   *  time but also see when their turn is about to send. */
  private onSilenceTickCb: ((msLeft: number) => void) | null = null;

  /* silence detection — for conversation mode where we auto-commit
   * the user's turn after they stop talking for SILENCE_TIMEOUT_MS. */
  private hasFinalResult = false;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceTickInterval: ReturnType<typeof setInterval> | null = null;
  private silenceStartedAt: number | null = null;
  /** True while the user has explicitly tapped Pause. resumeSilence()
   *  re-arms the countdown; stopCapture() / cancelCapture() clear it. */
  private silencePaused = false;
  // Bumped from 2s → 5s. ICs reading their notes / gathering their
  // thoughts mid-sentence would routinely trip the 2s threshold and
  // auto-send half-spoken turns. 5s is the sweet spot from user testing:
  // long enough to think, short enough that natural pauses don't feel
  // sluggish. The visible countdown (onSilenceTick) gives the user a
  // clear signal so they know they can take their time.
  private static readonly SILENCE_TIMEOUT_MS = 5_000;
  /** How often the silence-countdown tick fires while the timer runs.
   *  Tight enough for a smooth ring/number animation, loose enough not
   *  to thrash React renders. */
  private static readonly SILENCE_TICK_MS = 200;

  /** True when the browser exposes getUserMedia. */
  get isSupported(): boolean {
    return !!(typeof navigator !== 'undefined'
      && navigator.mediaDevices
      && navigator.mediaDevices.getUserMedia);
  }

  get isRecording(): boolean { return this.isActive; }

  /**
   * Initialise the Transcribe client. Lazy — call before startCapture.
   * Falls back to Web Speech API if Cognito isn't reachable (e.g. local
   * dev with no backend, or stack not yet updated).
   */
  async initialize(): Promise<void> {
    if (this.transcribeClient || this.useFallback) return;

    try {
      const cfg = await getTranscribeConfig();
      if (!cfg?.identityPoolId) throw new Error('Missing Cognito Identity Pool ID');

      const poolRegex = /^[a-z0-9-]+:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!poolRegex.test(cfg.identityPoolId)) throw new Error('Invalid Cognito pool ID format');

      this.config = cfg;
      console.log('[transcribe] fetching Cognito creds…', { region: cfg.region });
      this.credentialProvider = fromCognitoIdentityPool({
        clientConfig: { region: cfg.region },
        identityPoolId: cfg.identityPoolId,
      });
      // Pre-warm so the first user click is fast.
      await this.credentialProvider();
      console.log('[transcribe] Cognito creds ready');

      this.transcribeClient = new TranscribeStreamingClient({
        region: cfg.region,
        credentials: this.credentialProvider,
      });
    } catch (err) {
      console.warn('[transcribe] init failed, falling back to Web Speech API:', err);
      const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SR) {
        // Caller can still detect this via isSupported — but they won't get any
        // STT at all in this browser. We don't throw because we want
        // initialize() to be safe to call eagerly.
        console.warn('[transcribe] Web Speech API also unavailable in this browser');
        return;
      }
      this.useFallback = true;
    }
  }

  /**
   * Start push-to-talk capture. Each interim/final transcript goes through
   * onInterim. onError fires on any stream-level failure.
   */
  async startCapture(
    onInterim: (text: string) => void,
    onError: (error: string) => void,
    onSilence?: () => void,
    onSilenceTick?: (msLeft: number) => void,
  ): Promise<void> {
    if (this.isActive) return;

    this.transcript = '';
    this.onInterimCb = onInterim;
    this.onErrorCb = onError;
    this.onSilenceCb = onSilence ?? null;
    this.onSilenceTickCb = onSilenceTick ?? null;
    this.hasFinalResult = false;
    this.silencePaused = false;
    this.silenceStartedAt = null;
    this.isActive = true;
    this.connectionActive = false;

    if (this.useFallback) {
      try { this.startWebSpeechRecognition(onInterim, onError); }
      catch (err) {
        const friendly = friendlyVoiceError(err);
        console.error('[transcribe] WebSpeech start failed:', friendly, '— raw:', err);
        onError(friendly);
        this.isActive = false;
      }
      return;
    }

    if (!this.transcribeClient) {
      this.isActive = false;
      onError('Voice service couldn’t connect. Refresh the page and try again, or type your message instead.');
      return;
    }

    try {
      console.log('[transcribe] openMic…');
      await this.openMic();
      console.log('[transcribe] startStreaming…');
      await this.startStreaming();
      console.log('[transcribe] streaming live');
    } catch (err) {
      const friendly = friendlyVoiceError(err);
      // Always log the raw error for diagnostics — keep the cryptic SDK
      // detail in the console where engineers can see it; surface only
      // the friendly message to the user.
      console.error('[transcribe] startCapture error:', friendly, '— raw:', err);
      onError(friendly);
      this.isActive = false;
      this.cleanup();
    }
  }

  /** Stop capture and return the final accumulated transcript. */
  stopCapture(): string {
    this.isActive = false;
    this.connectionActive = false;
    this.clearSilenceTimer();
    this.closeStreamController();
    this.cleanup();

    if (this.recognition) {
      try { this.recognition.stop(); } catch { /* ignore */ }
      this.recognition = null;
    }

    const result = this.transcript.trim();
    this.transcript = '';
    return result;
  }

  /** Full teardown — call when the service is no longer needed. */
  dispose(): void {
    this.stopCapture();
    this.transcribeClient = null;
    this.config = null;
  }

  /* ── Web Speech API fallback (Chrome/Edge/Safari) ─────────────────────── */

  private startWebSpeechRecognition(
    onInterim: (text: string) => void,
    onError: (error: string) => void,
  ): void {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.recognition = new SR();
    this.recognition.lang = 'en-AU';
    this.recognition.interimResults = true;
    this.recognition.continuous = true;

    this.recognition.onresult = (event: any) => {
      let interim = '';
      let final = '';
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      this.transcript = final;
      onInterim((final + interim).trim());
    };
    this.recognition.onerror = (event: any) => {
      if (event.error !== 'aborted') onError(`Speech recognition error: ${event.error}`);
    };
    // Chrome auto-stops continuous recognition every ~60s — restart while active
    this.recognition.onend = () => {
      if (this.isActive && this.useFallback && this.recognition) {
        try { this.recognition.start(); } catch { /* ignore */ }
      }
    };
    this.recognition.start();
  }

  /* ── Mic ──────────────────────────────────────────────────────────────── */

  private async openMic(): Promise<void> {
    this.audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate:       16000,
        channelCount:     1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl:  true,
      },
    });

    // 16 kHz AudioContext matches what Transcribe expects. The TTS service
    // owns its own AudioContext (separate sample rate) — we don't share.
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new Ctor({ sampleRate: 16000, latencyHint: 'interactive' });
    if (this.audioContext!.state === 'suspended') await this.audioContext!.resume();
  }

  /* ── Transcribe Streaming WebSocket ────────────────────────────────────── */

  private async startStreaming(): Promise<void> {
    if (!this.audioStream || !this.audioContext) throw new Error('Audio not initialised');

    // SDK v3 keeps internal WebSocket state on the client — reuse can leave
    // a half-closed socket from the prior session and timeout the next
    // send(). Mint a fresh client each session.
    if (this.config && this.credentialProvider) {
      this.transcribeClient = new TranscribeStreamingClient({
        region: this.config.region,
        credentials: this.credentialProvider,
      });
    }

    // Set connectionActive=true BEFORE send() — newer SDKs need audio
    // flowing during the WebSocket handshake or the connection times out.
    const audioReadable = new ReadableStream({
      start: (controller) => {
        this.streamController = controller;
        this.setupAudioProcessingSync();
      },
      cancel: () => { this.connectionActive = false; },
    });

    this.connectionActive = true;

    const command = new StartStreamTranscriptionCommand({
      LanguageCode:                    LanguageCode.EN_AU,
      MediaEncoding:                   MediaEncoding.PCM,
      MediaSampleRateHertz:            16000,
      AudioStream:                     this.asyncIterator(audioReadable) as AsyncIterable<AudioStream>,
      EnablePartialResultsStabilization: true,
      PartialResultsStability:         'medium' as const,
    });

    const sendPromise = this.transcribeClient!.send(command);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Transcribe connection timed out (10s)')), 10_000),
    );
    const response = await Promise.race([sendPromise, timeoutPromise]);

    if (response.TranscriptResultStream) {
      this.processResults(response.TranscriptResultStream);
    } else {
      this.connectionActive = false;
      throw new Error('No transcription stream received');
    }
  }

  /**
   * Wire the mic to the Transcribe stream. Tries AudioWorklet first
   * (modern, runs off the main thread) and falls back to the deprecated
   * ScriptProcessorNode if AudioWorklet isn't available or fails (CSP
   * sometimes blocks the inline-Blob worklet module).
   */
  private setupAudioProcessingSync(): void {
    if (!this.audioContext || !this.audioStream || !this.streamController) return;
    this.source = this.audioContext.createMediaStreamSource(this.audioStream);

    let useWorklet = false;
    if (this.audioContext.audioWorklet) {
      try {
        if (!this.workletRegistered) {
          const blob = new Blob([WORKLET_PROCESSOR_SRC], { type: 'application/javascript' });
          const url = URL.createObjectURL(blob);
          // addModule is async but we need synchronous setup for the
          // ReadableStream start() callback. We optimistically register
          // and disable use if it fails — the ScriptProcessor fallback
          // kicks in below.
          this.audioContext.audioWorklet.addModule(url).then(() => {
            this.workletRegistered = true;
            URL.revokeObjectURL(url);
          }).catch((err) => {
            console.warn('[transcribe] AudioWorklet addModule failed:', err);
            URL.revokeObjectURL(url);
          });
        }
        if (this.workletRegistered) {
          this.workletNode = new AudioWorkletNode(this.audioContext, 'cima-pcm-chunk-processor');
          this.workletNode.port.onmessage = (e: MessageEvent<Float32Array>) => {
            if (!this.isActive || !this.connectionActive || !this.streamController) return;
            // Pause guard — when the user has tapped Pause, keep the
            // mic open + the Transcribe connection alive but DON'T
            // forward audio. Without this, AWS keeps transcribing
            // and the chat composer fills with whatever the user
            // says during their thinking window. Resume re-enables
            // forwarding from the next audio frame; no reconnect.
            if (this.silencePaused) return;
            const pcm = float32ToInt16(e.data);
            try { this.streamController.enqueue({ AudioEvent: { AudioChunk: pcm } }); }
            catch { this.connectionActive = false; }
          };
          this.source.connect(this.workletNode);
          this.workletNode.connect(this.audioContext.destination);
          useWorklet = true;
        }
      } catch (err) {
        console.warn('[transcribe] AudioWorklet setup failed, falling back:', err);
      }
    }

    if (!useWorklet) this.setupScriptProcessorFallback();
  }

  private setupScriptProcessorFallback(): void {
    if (!this.audioContext || !this.source || !this.streamController) return;
    this.processor = this.audioContext.createScriptProcessor(2048, 1, 1);

    const CHUNK_SAMPLES = 1600; // 100 ms @ 16 kHz
    let buffer = new Float32Array(0);

    this.processor.onaudioprocess = (e) => {
      if (!this.isActive || !this.connectionActive || !this.streamController) return;
      // Pause guard — see worklet handler comment above. Drop the
      // current audio frame entirely while paused so AWS Transcribe
      // never sees the user's "thinking out loud".
      if (this.silencePaused) {
        // Reset buffer too so we don't ship stale-but-pre-pause audio
        // when the user hits Resume.
        buffer = new Float32Array(0);
        return;
      }
      const input = e.inputBuffer.getChannelData(0);
      const merged = new Float32Array(buffer.length + input.length);
      merged.set(buffer);
      merged.set(input, buffer.length);
      buffer = merged;
      while (buffer.length >= CHUNK_SAMPLES) {
        const chunk = buffer.slice(0, CHUNK_SAMPLES);
        buffer = buffer.slice(CHUNK_SAMPLES);
        const pcm = float32ToInt16(chunk);
        try { this.streamController!.enqueue({ AudioEvent: { AudioChunk: pcm } }); }
        catch { this.connectionActive = false; }
      }
    };
    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
  }

  /* ── Result processing ────────────────────────────────────────────────── */

  private async processResults(stream: AsyncIterable<unknown>): Promise<void> {
    let eventCount = 0;
    try {
      for await (const event of stream as AsyncIterable<Record<string, unknown>>) {
        if (!this.isActive) break;
        eventCount++;

        const txEvent = event.TranscriptEvent as
          | { Transcript?: { Results?: Array<{ IsPartial?: boolean; Alternatives?: Array<{ Transcript?: string }> }> } }
          | undefined;

        if (!txEvent) {
          if (event.BadRequestException || event.InternalFailureException ||
              event.LimitExceededException || event.ServiceUnavailableException) {
            const errKey = Object.keys(event).find((k) => k.endsWith('Exception'));
            const rawMsg = (event[errKey!] as { Message?: string })?.Message || 'Transcription error';
            // Map AWS exception name → user-friendly text. Keep the raw
            // message in the console so engineers can debug, surface a
            // short instruction to the user.
            console.warn('[transcribe] AWS exception:', errKey, rawMsg);
            this.onErrorCb?.(friendlyVoiceError(new Error(`${errKey}: ${rawMsg}`)));
            break;
          }
          continue;
        }

        const results = txEvent.Transcript?.Results;
        if (!results?.length) continue;
        const result = results[0];
        const text = result.Alternatives?.[0]?.Transcript || '';
        if (!text.trim()) continue;

        if (result.IsPartial) {
          this.onInterimCb?.((this.transcript + text).trim());
          if (text.trim()) this.resetSilenceTimer();
        } else {
          this.transcript += text + ' ';
          this.onInterimCb?.(this.transcript.trim());
          this.hasFinalResult = true;
          this.resetSilenceTimer();
        }
      }
    } catch (err) {
      if (this.isActive) {
        console.warn('[transcribe] processResults error:', err);
        this.onErrorCb?.(friendlyVoiceError(err));
      }
    }
  }

  /* ── Silence detection ────────────────────────────────────────────────── */

  private resetSilenceTimer(): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    if (this.silenceTickInterval) clearInterval(this.silenceTickInterval);
    if (!this.onSilenceCb || !this.isActive) return;
    // If the user has tapped Pause, don't arm the auto-send timer at all.
    // resumeSilence() will re-call this method to start a fresh window.
    if (this.silencePaused) {
      this.silenceStartedAt = null;
      this.onSilenceTickCb?.(TranscribeService.SILENCE_TIMEOUT_MS);
      return;
    }

    this.silenceStartedAt = Date.now();
    // Tick the countdown so the UI can show "auto-sending in N s".
    this.onSilenceTickCb?.(TranscribeService.SILENCE_TIMEOUT_MS);
    this.silenceTickInterval = setInterval(() => {
      if (!this.isActive || this.silencePaused || this.silenceStartedAt == null) return;
      const elapsed = Date.now() - this.silenceStartedAt;
      const msLeft = Math.max(0, TranscribeService.SILENCE_TIMEOUT_MS - elapsed);
      this.onSilenceTickCb?.(msLeft);
    }, TranscribeService.SILENCE_TICK_MS);

    this.silenceTimer = setTimeout(() => {
      // Only fire if we actually got a final result (user spoke real words)
      if (this.hasFinalResult && this.isActive && this.transcript.trim() && !this.silencePaused) {
        this.onSilenceCb?.();
      }
    }, TranscribeService.SILENCE_TIMEOUT_MS);
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.silenceTickInterval) {
      clearInterval(this.silenceTickInterval);
      this.silenceTickInterval = null;
    }
    this.silenceStartedAt = null;
  }

  /** Pause the auto-send countdown without stopping audio capture.
   *  The mic keeps listening; we just don't auto-commit when the user
   *  goes quiet. Used by the "Pause" button in the conversation overlay
   *  so the IC can gather their thoughts mid-turn. */
  pauseSilence(): void {
    this.silencePaused = true;
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    if (this.silenceTickInterval) clearInterval(this.silenceTickInterval);
    this.silenceTimer = null;
    this.silenceTickInterval = null;
    this.silenceStartedAt = null;
    this.onSilenceTickCb?.(TranscribeService.SILENCE_TIMEOUT_MS);
  }

  /** Resume the auto-send countdown. Restarts a fresh 5s window from
   *  now — same as if the user had just finished a phrase. */
  resumeSilence(): void {
    this.silencePaused = false;
    if (this.isActive) this.resetSilenceTimer();
  }

  /** True while the user has tapped Pause and the auto-send countdown
   *  is suppressed. Audio capture continues regardless. */
  get isSilencePaused(): boolean { return this.silencePaused; }

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  private async *asyncIterator(stream: ReadableStream): AsyncIterable<unknown> {
    const reader = stream.getReader();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        yield value;
      }
    } finally {
      reader.releaseLock();
    }
  }

  private closeStreamController(): void {
    if (this.streamController) {
      try { this.streamController.close(); } catch { /* already closed */ }
      this.streamController = null;
    }
  }

  private cleanup(): void {
    this.connectionActive = false;
    if (this.workletNode) { try { this.workletNode.port.close(); } catch {} this.workletNode.disconnect(); this.workletNode = null; }
    if (this.processor) { this.processor.disconnect(); this.processor = null; }
    if (this.source) { this.source.disconnect(); this.source = null; }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
      this.workletRegistered = false;
    }
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((t) => t.stop());
      this.audioStream = null;
    }
  }
}

/* ── PCM helpers + worklet source ──────────────────────────────────────── */

function float32ToInt16(f32: Float32Array): Uint8Array {
  const out = new Uint8Array(f32.length * 2);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    const v = s < 0 ? s * 0x8000 : s * 0x7fff;
    out[i * 2] = v & 0xff;
    out[i * 2 + 1] = (v >> 8) & 0xff;
  }
  return out;
}

const WORKLET_PROCESSOR_SRC = `
class CimaPcmChunkProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array(0);
    this.CHUNK_SAMPLES = 1600;
  }
  process(inputs) {
    const input = inputs[0]?.[0];
    if (!input) return true;
    const merged = new Float32Array(this.buffer.length + input.length);
    merged.set(this.buffer);
    merged.set(input, this.buffer.length);
    this.buffer = merged;
    while (this.buffer.length >= this.CHUNK_SAMPLES) {
      const chunk = this.buffer.slice(0, this.CHUNK_SAMPLES);
      this.buffer = this.buffer.slice(this.CHUNK_SAMPLES);
      this.port.postMessage(chunk);
    }
    return true;
  }
}
registerProcessor('cima-pcm-chunk-processor', CimaPcmChunkProcessor);
`;

// Module-level singleton — one Transcribe service per app instance.
let _instance: TranscribeService | null = null;
export function getTranscribe(): TranscribeService {
  if (!_instance) _instance = new TranscribeService();
  return _instance;
}
